import { useEffect, useRef } from 'react';
import { RouteFollowPoi } from '../types';
import {
  playOffRouteAlert,
  playBackOnRouteAlert,
  playNearbyPoiAlert,
  vibratePattern,
  resumeAudioContext,
} from '../services/audioService';

const LS_KEY = 'nav_audio_enabled';

interface NavigationAlertState {
  isOffRoute: boolean;
  nearbyPoi: RouteFollowPoi | null;
  isFollowing: boolean;
}

function isAudioEnabled(): boolean {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v !== 'false'; // default true
  } catch {
    return true;
  }
}

export function setAudioEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(LS_KEY, String(enabled));
  } catch {}
}

export function getAudioEnabled(): boolean {
  return isAudioEnabled();
}

/**
 * Hook som spiller lyd- og haptikkvarsler under navigasjon.
 *
 * - Off-route: varselslyd + vibrasjon ved isOffRoute, maks 1 per 30s
 * - Tilbake på rute: bekreftelseslyd
 * - Nearby POI: pling + vibrasjon, maks 1 per unike POI
 */
export function useNavigationAlerts(state: NavigationAlertState): void {
  const wasOffRouteRef = useRef(false);
  const lastOffRouteAlertRef = useRef(0);
  const alertedPoiIdsRef = useRef(new Set<string>());

  // Resume audio context when following starts
  useEffect(() => {
    if (state.isFollowing) resumeAudioContext();
  }, [state.isFollowing]);

  // Reset state when navigation stops
  useEffect(() => {
    if (!state.isFollowing) {
      wasOffRouteRef.current = false;
      lastOffRouteAlertRef.current = 0;
      alertedPoiIdsRef.current.clear();
    }
  }, [state.isFollowing]);

  // Off-route alerts
  useEffect(() => {
    if (!state.isFollowing || !isAudioEnabled()) return;

    if (state.isOffRoute && !wasOffRouteRef.current) {
      // Ble nettopp off-route
      const now = Date.now();
      if (now - lastOffRouteAlertRef.current > 30_000) {
        playOffRouteAlert();
        vibratePattern([200, 100, 200]);
        lastOffRouteAlertRef.current = now;
      }
    } else if (!state.isOffRoute && wasOffRouteRef.current) {
      // Tilbake på ruten
      playBackOnRouteAlert();
      vibratePattern(100);
    }

    wasOffRouteRef.current = state.isOffRoute;
  }, [state.isFollowing, state.isOffRoute]);

  // Nearby POI alerts
  useEffect(() => {
    if (!state.isFollowing || !isAudioEnabled()) return;
    if (!state.nearbyPoi) return;

    if (!alertedPoiIdsRef.current.has(state.nearbyPoi.id)) {
      alertedPoiIdsRef.current.add(state.nearbyPoi.id);
      playNearbyPoiAlert();
      vibratePattern(100);
    }
  }, [state.isFollowing, state.nearbyPoi?.id]);
}
